import React, { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth as useClerkAuth, ClerkLoaded, ClerkLoading } from '@clerk/react'
import TerraClerkProvider from './components/TerraClerkProvider'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import ResetPassword from './pages/ResetPassword'
import './App.css'

const AuthenticatedApp = React.lazy(() => import('./AuthenticatedApp'))

const PUBLIC_PATHS = new Set(['/login', '/signup', '/reset_password'])

function fullScreenLoading(message) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontSize: '16px',
      color: '#666',
    }}
    >
      {message || 'Loading...'}
    </div>
  )
}

/**
 * Top-level gate. Unauthenticated visitors see only the public Clerk pages —
 * none of the AuthProvider / ApiClient / appConfig surface ships with the
 * initial bundle. Once Clerk reports a signed-in session we lazy-load the
 * AuthenticatedApp chunk which in turn pulls in the API client, hydrates the
 * Flask user profile, and mounts the private routes.
 */
function AppRoutes() {
  const location = useLocation()
  const { isLoaded, isSignedIn } = useClerkAuth()

  const isPublicRoute = PUBLIC_PATHS.has(location.pathname)

  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset_password" element={<ResetPassword />} />
      </Routes>
    )
  }

  if (!isLoaded) {
    return fullScreenLoading()
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  return (
    <Suspense fallback={fullScreenLoading()}>
      <AuthenticatedApp />
    </Suspense>
  )
}

function App() {
  return (
    <Router>
      <TerraClerkProvider>
        <ClerkLoading>{fullScreenLoading('Loading…')}</ClerkLoading>
        <ClerkLoaded>
          <AppRoutes />
        </ClerkLoaded>
      </TerraClerkProvider>
    </Router>
  )
}

export default App
